/**
 * The accord between the files of this sub-tree (refonte modulaire §9, §10.2;
 * issues #55 and #57).
 *
 * Every `data-testid` more than one file reads is declared HERE, exactly as
 * `protocoles/contracts.ts` does for the frise and the step editor - and for
 * the same reason: this sub-tree is written by several hands and read by two
 * suites that do not compile it (`e2e/tests/experiences.spec.ts` and
 * `e2e/screenshots-experiences.mjs` recopy these strings). A mark renamed on
 * one side has to turn something red on the other, and a mark spelled twice
 * would turn nothing red at all.
 *
 * What is NOT here: every mark the versioned layer emits from
 * `EXPERIENCE_WORDING` - `experience-carte`, `experiences-grille`,
 * `experience-entete`, `experience-nom`, `experience-onglet-<id>`,
 * `experience-boite-enregistrer` and their neighbours. Those belong to
 * `versioned-labels.ts`, which states the rule: every mark of the screen is
 * `${entity}-...` or `${list}-...`, so changing one of the two prefixes changes
 * every selector of the screen at once.
 */
export const EXPERIENCE_IDS = {
  /** The card: the line of facets, the usage line, one warning per retired reference. */
  cardFacets: 'experience-carte-facettes',
  cardUsage: 'experience-carte-usage',
  cardWarning: 'experience-carte-avertissement',

  /** The three own filters of §15: two in the visible row, one in the panel. */
  filterProtocol: 'experience-filtre-protocole',
  filterPersona: 'experience-filtre-persona',
  filterLink: 'experience-filtre-lien',

  /** « Créer un lien » - the anchor of the head that leads to Réglages > Partage. */
  createLink: 'experience-creer-lien',

  /** The Timeline tab: the scene, the rail, and the gestures of §9.1. */
  timeline: 'experience-timeline',
  addPage: 'experience-ajouter-page',
  editPage: 'experience-editer-page',

  /**
   * The SCENE (§9.1, issue #57): the selected page large in the middle, its two
   * neighbours entire and small, the edges between them.
   *
   * `scenePage` marks the centre and carries `data-page-id`, `data-kind` and
   * `data-format`; a neighbour carries the first two plus `data-side`. What is
   * INSIDE them is found by `READER_TEST_IDS`, the reader's own register: a
   * second mark on the same node would be a second name for one thing.
   */
  scene: 'experience-scene',
  scenePage: 'experience-scene-page',
  sceneNeighbour: 'experience-scene-voisine',
  sceneEdge: 'experience-scene-arete',
  sceneTransition: 'experience-scene-transition',
  insertPage: 'experience-inserer-page',
  /** L'icône de copie de §4.2, dans la gouttière et juste avant la croix (#118). */
  duplicatePage: 'experience-dupliquer-page',
  deletePage: 'experience-supprimer-page',
  deletePageConfirm: 'experience-supprimer-page-confirmer',
  /** « Téléphone | Écran » - the ratio of the scene, a state of the tab alone. */
  format: 'experience-format',

  /**
   * La page de retour d'expérience (retour-experience.md §7.1, issue #176) : la
   * pastille « Retour » - sur la scène, la vignette voisine, le rail et le
   * bandeau de l'éditeur de page - et le petit menu « Page de contenu » / « Page
   * de retour » que le « + » de fin ouvre tant qu'elle est absente. Chaque
   * entrée du menu porte `data-page-kind` (`content` ou `feedback`).
   */
  feedbackBadge: 'experience-badge-retour',
  /**
   * Une page jouée dans la conversation (prolongation.md §6, issue #186) : la
   * pastille « Conversation » - au patron de celle de « Retour », aux mêmes quatre
   * emplacements -, la page rendue EN FIL (le fil, sa bulle témoin, la bulle
   * pointillée du contexte résolu, les éléments de la page) et son avertissement
   * quand le contexte est vide.
   */
  conversationBadge: 'experience-badge-conversation',
  conversationThread: 'experience-fil',
  conversationWitness: 'experience-fil-temoin',
  conversationContext: 'experience-fil-contexte',
  conversationItems: 'experience-fil-elements',
  addPageMenu: 'experience-ajouter-page-menu',
  addPageOption: 'experience-ajouter-page-option',

  /** The rail of numbered pastilles under the scene: one per page, in order. */
  rail: 'experience-rail',
  railPage: 'experience-rail-page',
  /**
   * La bulle d’une pastille, en variante compacte (issue #140).
   *
   * Le bandeau de l’éditeur de page est le seul hôte qui en pose une : sur la
   * Timeline la pastille porte déjà son nom en clair, et une bulle qui
   * répéterait ce qu’on lit déjà serait la migration mécanique que le « + » du
   * bandeau a coûtée à l’issue 103.
   */
  railBubble: 'experience-rail-bulle',

  /** The protocol page (§9.5): its card, and the selector « Remplacer » opens. */
  protocolCard: 'experience-protocole-carte',
  protocolReplace: 'experience-remplacer-protocole',
  protocolPicker: 'experience-protocole-selecteur',
  protocolSearch: 'experience-protocole-recherche',
  protocolOption: 'experience-protocole-option',
  /** The pill-menu of the version a page plays (vitrine.md §3.1): its menu, options and signal hang off it. */
  protocolPin: 'experience-protocole-version',
  protocolClear: 'experience-protocole-aucun',

  /**
   * The « Visuel global » bar (§9.4) - collapsed, then its five columns.
   *
   * `themeBar` carries `data-level`, `global` here and `page` when the page
   * editor of #58 mounts the very same component on a page's overrides. One
   * register for the two states, because they are one component.
   */
  themeBar: 'experience-visuel',
  themeSummary: 'experience-visuel-resume',
  themeToggle: 'experience-visuel-regler',
  themePanel: 'experience-visuel-panneau',
  themeField: 'experience-visuel-champ',
  themeContrast: 'experience-visuel-contraste',
  themeReset: 'experience-visuel-revenir',
  themeBackground: 'experience-visuel-fond',

  /** The Persona tab (§10.2), its two states, and the selector it opens. */
  persona: 'experience-persona',
  personaCard: 'experience-persona-carte',
  personaEmpty: 'experience-persona-vide',
  personaChoose: 'experience-persona-choisir',
  personaPicker: 'experience-persona-selecteur',
  personaSearch: 'experience-persona-recherche',
  personaOption: 'experience-persona-option',
  personaPin: 'experience-persona-version',
  personaOpen: 'experience-persona-ouvrir',
  overlay: 'experience-surcouche',
  overlayVariable: 'experience-surcouche-variable',
  directive: 'experience-directive',

  /** The two encarts of the save box, and the « Ouvrir » of a refused line. */
  refusals: 'experience-refus',
  warnings: 'experience-avertissement',
  refusalOpen: 'experience-refus-ouvrir',
  /** One line of the yellow encart, with `data-kind` - the warnings of the noyau among them (#151). */
  warning: 'experience-avertissement-ligne',
  /** One refused line of « Passer en production » in the red encart, with `data-kind` (#154). */
  refusal: 'experience-refus-ligne',
  /**
   * « Ouvrir le protocole » - the link that ends the line of a protocol with no
   * reading for the analyst, yellow or red, and leads to that protocol's
   * reading, `?vue=lecture` (noyau.md §3.7, N47; issue #154). Since #158 it
   * ends the line of an orphan direction too, and leads to the conduite, the
   * editor's view by default (§8.6 of the refonte).
   */
  openProtocolReading: 'experience-ouvrir-protocole',
  /**
   * « Passer en production » could not ask the server about the protocol of
   * the version it moves - its reading, its directions: said, and blocking
   * nothing (review of !474).
   */
  pointerCheckFailed: 'experience-verification-impossible',
  /**
   * A dry run read again in the background, its previous answer kept greyed
   * and still blocking (second review of !474, `dry-run-frame.tsx`).
   */
  dryRunRechecking: 'experience-verification-en-cours',

  /** The page editor, a seam until issue #58. */
  pageEditor: 'experience-page-editeur',
} as const;
